import { useState, useEffect } from "react";
import { Loader2, Plus, Ticket, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/admin/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { formatGs, formatDate } from "@/lib/format";

const EMPTY = { code: "", discount_type: "percent", discount_value: 10, valid_until: "", usage_limit: 0, active: true };

const Coupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => { setLoading(true); api.get("/admin/coupons").then((r) => setCoupons(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.code) return toast.error("Ingresá un código");
    try {
      await api.post("/admin/coupons", { ...form, valid_until: form.valid_until || null });
      toast.success("Cupón creado");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (id) => { await api.delete(`/admin/coupons/${id}`); toast.success("Eliminado"); load(); };

  return (
    <div>
      <PageHeader title="Cupones" subtitle="Descuentos por porcentaje o valor fijo"
        action={<Button onClick={() => { setForm(EMPTY); setOpen(true); }} className="rounded-lg bg-indigo-600 hover:bg-indigo-700" data-testid="new-coupon-btn"><Plus className="h-4 w-4 mr-1" /> Nuevo cupón</Button>} />

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>
        : coupons.length === 0 ? <EmptyState icon={Ticket} text="Sin cupones." />
        : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {coupons.map((c) => (
              <div key={c.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`coupon-${c.code}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-heading font-bold text-lg text-slate-900">{c.code}</p>
                    <p className="text-sm text-indigo-600 font-semibold">{c.discount_type === "percent" ? `${c.discount_value}% off` : `${formatGs(c.discount_value)} off`}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${c.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{c.active ? "Activo" : "Inactivo"}</span>
                </div>
                <div className="mt-3 text-xs text-slate-500 space-y-1">
                  <p>Usos: {c.used_count || 0}{c.usage_limit ? ` / ${c.usage_limit}` : " (ilimitado)"}</p>
                  <p>Vence: {c.valid_until ? formatDate(c.valid_until) : "Sin vencimiento"}</p>
                </div>
                <Button variant="outline" size="sm" className="rounded-lg mt-3 text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => del(c.id)} data-testid={`del-coupon-${c.code}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuevo cupón</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Código</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="rounded-lg mt-1" data-testid="coupon-code" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tipo</Label>
                <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger className="rounded-lg mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="percent">Porcentaje %</SelectItem><SelectItem value="fixed">Monto fijo ₲</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Valor</Label><Input type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: Number(e.target.value) })} className="rounded-lg mt-1" data-testid="coupon-value" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Vencimiento</Label><Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className="rounded-lg mt-1" /></div>
              <div><Label>Límite de usos</Label><Input type="number" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: Number(e.target.value) })} className="rounded-lg mt-1" placeholder="0 = ilimitado" /></div>
            </div>
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Activo</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-lg">Cancelar</Button>
            <Button onClick={save} className="rounded-lg bg-indigo-600 hover:bg-indigo-700" data-testid="save-coupon">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Coupons;
